/*
 * Fires a page_view event on every route change and a session_start once
 * per browser session. Uses a fire-and-forget POST to /analytics/track — it
 * is deliberately isolated from the app's main axios interceptors so that
 * an auth error can never break a page view record.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

const SESSION_KEY = "sine_ana_session";

function ensureSession() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(SESSION_KEY, sid);
      return { sid, isNew: true };
    }
    return { sid, isNew: false };
  } catch {
    return { sid: "anon", isNew: false };
  }
}

function getProvinceId() {
  try {
    const raw = localStorage.getItem("sine_province");
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p?.id || null;
  } catch {
    return null;
  }
}

async function track(payload) {
  try {
    await api.post("/analytics/track", payload);
  } catch {
    // never surface errors from tracking
  }
}

export default function useAnalyticsTracking() {
  const location = useLocation();
  const lastPath = useRef(null);

  // Route-based page_view tracking
  useEffect(() => {
    const path = location.pathname + location.search;
    if (lastPath.current === path) return;
    lastPath.current = path;

    // Skip admin panel entirely so noise doesn't dilute editorial metrics
    if (path.startsWith("/admin")) return;

    const { sid, isNew } = ensureSession();
    const province_id = getProvinceId();

    if (isNew) {
      track({
        event_type: "session_start",
        province_id,
        meta: {
          sid,
          referrer: document.referrer || null,
          ua: navigator.userAgent,
          lang: navigator.language,
          screen: `${window.screen.width}x${window.screen.height}`,
        },
      });
    }

    // Entity extraction for content detail pages (best-effort)
    let entity_type = null;
    let entity_id = null;
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts[0] === "haber" && parts[1]) {
      entity_type = "news";
      entity_id = parts[1];
    } else if (parts[0] === "yazi" && parts[1]) {
      // Individual column article click
      entity_type = "column";
      entity_id = parts[1];
    } else if ((parts[0] === "kose-yazilari" || parts[0] === "kose-yazarlari") && parts[1]) {
      // Columnist profile page view (tracked separately from article views)
      entity_type = "columnist";
      entity_id = parts[1];
    } else if (parts[0] === "etkinlikler" && parts[1]) {
      entity_type = "event";
      entity_id = parts[1];
    } else if (parts[0] === "video" && parts[1]) {
      entity_type = "video";
      entity_id = parts[1];
    }

    // Parse UTM params from current URL
    let utm = {};
    try {
      const sp = new URLSearchParams(location.search);
      for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
        const v = sp.get(k);
        if (v) utm[k] = v.slice(0, 100);
      }
    } catch {
      /* no-op */
    }

    track({
      event_type: "page_view",
      entity_type,
      entity_id,
      province_id,
      meta: {
        sid,
        path,
        title: document.title || null,
        referrer: document.referrer || null,
        ...utm,
      },
    });
  }, [location.pathname, location.search]);
}
